import React, { createContext, ReactNode, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useShoppingCart } from './shoppingcart.provider';
import { useCart } from '../pages/Purchase/LongSteel/ShoppingCartMVP/useCart';
import { useBulkErrorToast } from '../services/MVP/useBulkErrorToast';

interface CallFunctionProps {
  action: () => void;
  hasItemOnCart?: boolean;
}

interface HandleNavigationProps {
  path: string;
  hasItemOnCart?: boolean;
}

interface NavigationGuardCtxProps {
  showModal: boolean;
  handleNavigation: (props: HandleNavigationProps) => boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  handleCallFunctions: (props: CallFunctionProps) => void;
}

const defaultCtxValue: NavigationGuardCtxProps = {
  showModal: false,
  handleNavigation: () => false,
  confirmNavigation: () => null,
  cancelNavigation: () => null,
  handleCallFunctions: (props: CallFunctionProps) => props.action
};

const NavigationGuardContext =
  createContext<NavigationGuardCtxProps>(defaultCtxValue);

export const NavigationGuardProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { hasDirtyOnCartState, dirtyCart } = useShoppingCart();
  const { addBulkGetCart } = useCart();
  const { dispatchBulkErrorToast } = useBulkErrorToast();

  const [showModal, setShowModal] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState<
    string | null
  >(null);
  const [pendingAction, setPendingAction] = React.useState<() => void | null>();

  const restrictedPaths = [
    '/purchase/long-steel/commerce/shopping-cart',
    '/purchase/long-steel/commerce/checkout'
  ];

  const handleNavigation = ({
    path,
    hasItemOnCart = false
  }: HandleNavigationProps) => {
    if (restrictedPaths.includes(location.pathname) && hasItemOnCart) {
      setPendingNavigation(path);
      setShowModal(true);
      return false;
    }
    history.push(path);
    return true;
  };

  const handleCallFunctions = ({
    action,
    hasItemOnCart = false
  }: CallFunctionProps) => {
    if (restrictedPaths.includes(location.pathname) && hasItemOnCart) {
      setPendingAction(() => action);
      setShowModal(true);
      return false;
    }
    return action();
  };

  const confirmNavigation = async () => {
    const hasDirty = hasDirtyOnCartState();
    if (pendingNavigation) {
      history.push(pendingNavigation);
      setShowModal(false);
      setPendingNavigation(null);
    }
    if (pendingAction) {
      pendingAction();
      setShowModal(false);
      setPendingAction(undefined);
    }
    if (hasDirty) {
      const response = await addBulkGetCart({
        items: dirtyCart,
        withOptions: false,
        withPrice: false,
        withoutMimimum: true
      });

      dispatchBulkErrorToast(
        response?.cart?.items ?? [],
        response?.errors ?? []
      );
    }
  };

  const cancelNavigation = () => {
    setShowModal(false);
  };

  return (
    <NavigationGuardContext.Provider
      value={{
        showModal,
        handleNavigation,
        handleCallFunctions,
        confirmNavigation,
        cancelNavigation
      }}
    >
      {children}
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = () => useContext(NavigationGuardContext);
